import { useRouter } from 'next/router';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';
import { User } from 'lib/types/users';
import { useUserLists } from 'lib/contexts/userListsContext';
import { useApi } from 'lib/contexts/ApplicationState';
import { addCreatorsToListMessage } from 'features/UserLists/AddCreatorsMessage';
import { ListUserSearchModal } from 'components/ListUserSearch';
import { useEffect, useState } from 'react';
import { Job } from 'lib/types/jobs';
import { handleFindMoreApplicants } from 'lib/helpers';

interface Props {
  publicId?: string;
  onCreatorsAdd: () => void;
}

export const AddUsersToList = ({ publicId, onCreatorsAdd }: Props) => {
  const { showModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const API = useApi();
  const router = useRouter();
  const { lists } = useUserLists();
  const [job, setJob] = useState<Job>();

  useEffect(() => {
    const fetchJobDetails = async (jobId: number) => {
      const response = await API.getJob(jobId);
      setJob(response);
    };

    if (publicId) {
      const list = lists?.find((ls) => ls.publicId === publicId);
      if (list && list.jobId) {
        fetchJobDetails(list.jobId);
      } else {
        setJob(undefined);
      }
    }
  }, [publicId, lists]);

  if (!lists) {
    return null;
  }

  const save = async (users: User[], listId: number) => {
    if (!listId) {
      enqueueSnackbar('No list specified', { variant: 'error' });
      return;
    }
    try {
      const list = lists?.filter((l) => l.id === listId)[0];
      await API.bulkAddUsersToList(users, listId);
      enqueueSnackbar(`${users.length} ${users.length > 1 ? 'creators' : 'creator'} added to ${list.name}`, { variant: 'success' });
      onCreatorsAdd();
    } catch (err) {
      enqueueSnackbar(`There was an error saving the list ${err}`, { variant: 'error' });
    }
  };

  const addUsersToList = () => {
    const modal = showModal(ListUserSearchModal, {
      onSave: save,
      onClose: () => {
        modal.destroy();
      },
      lists,
      publicId,
    });
  };

  const clearFilters = () => {
    const listName = lists.find((ls) => ls.publicId === publicId)?.name;
    if (listName) {
      enqueueSnackbar(addCreatorsToListMessage(listName), { variant: 'info' });
    }
    router.push('/creators');
  };

  return (
    <div>
      {job?.id && (
        <div className="flex justify-center pt-2">
          <button className="btn-secondary-outlined" type="button" onClick={() => handleFindMoreApplicants(job.roles || [], job.locations || [])}>
            Find more candidates
          </button>
        </div>
      )}
      <div className="flex gap-2 justify-center py-4">
        <button className="btn-primary-outlined" type="button" onClick={addUsersToList}>
          Add creators by name
        </button>
        <button className="btn-primary-outlined" type="button" onClick={clearFilters}>
          Add creators by filter
        </button>
      </div>
    </div>
  );
};
